import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import StickyCta from "@/components/site/StickyCta";
import { INDUSTRIES, industryBySlug } from "@/data/industries";
import { pillarBySlug } from "@/data/platform";
import { PillarIcon } from "@/components/platform/PlatformPrimitives";

function IndustryCard({ industry, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
    >
      <Link
        to={`/industries/${industry.slug}`}
        className="group block rounded-2xl overflow-hidden border border-[#E8E6E1] bg-[#FDFCFB] hover:-translate-y-0.5 hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.18)] transition-all"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F5F4F0]">
          <img
            src={industry.image}
            alt={industry.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          {industry.brand && (
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#FDFCFB]/85 backdrop-blur-sm font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#1A1A1A]">
              {industry.brand}
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] tracking-tight">
            {industry.name}
          </h3>
          <p className="mt-1 text-sm text-[#6B6A68]">{industry.tagline}</p>
        </div>
      </Link>
    </motion.div>
  );
}

export function IndustriesIndex() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  return (
    <main className="bg-[#FDFCFB] text-[#1A1A1A] min-h-screen">
      <Nav />
      <section className="pt-32 md:pt-40 pb-12 max-w-7xl mx-auto px-6 md:px-12">
        <div className="caption mb-6">Industries · Suites</div>
        <h1 className="font-serif-display text-[40px] sm:text-6xl md:text-7xl tracking-tighter leading-[1.02] max-w-4xl">
          Bundled for your <span className="italic">industry.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-[#6B6A68] text-base md:text-lg leading-relaxed">
          Eight pre-bundled suites. Each one combines the right products from
          across the platform for a specific vertical — so you can run an entire
          business, not just one department.
        </p>
      </section>
      <section className="py-12 md:py-16 bg-[#F5F4F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((ind, i) => (
              <IndustryCard key={ind.slug} industry={ind} i={i} />
            ))}
          </div>
        </div>
      </section>
      <Footer />
      <StickyCta />
    </main>
  );
}

export default function Industry() {
  const { slug } = useParams();
  const industry = industryBySlug[slug];

  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  if (!industry) {
    return (
      <main className="bg-[#FDFCFB] min-h-screen">
        <Nav />
        <div className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center">
          <div className="caption mb-4">404 — Industry not found</div>
          <h1 className="font-serif-display text-4xl tracking-tight">
            This industry suite doesn't exist.
          </h1>
          <Link
            to="/industries"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> All industries
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="bg-[#FDFCFB] text-[#1A1A1A] min-h-screen">
      <Nav />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/industries" className="inline-flex items-center gap-2 text-sm text-[#6B6A68] hover:text-[#1A1A1A] mb-10 group">
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          All industries
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="caption mb-3">Industry Suite · {industry.name}</div>
            {industry.brand && (
              <div className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-[#1A1A1A] mb-3">
                {industry.brand}
              </div>
            )}
            <h1 className="font-serif-display text-[40px] sm:text-6xl md:text-7xl tracking-tighter leading-[1.02]">
              {industry.tagline}
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={industry.marketingSlug ? `/products/${industry.marketingSlug}` : "/#contact"}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm font-medium hover:bg-[#333] transition-all"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#7EE787]" />
                Explore the suite
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/#contact"
                className="px-7 py-4 rounded-full border border-[#1A1A1A]/20 text-sm hover:bg-[#1A1A1A] hover:text-[#FDFCFB] transition-all"
              >
                Book a demo
              </Link>
            </div>
            {industry.outcome && (
              <div className="mt-10 flex items-baseline gap-5 border-t border-[#E8E6E1] pt-6 max-w-md">
                <div className="font-serif-display text-5xl tracking-tight">{industry.outcome.value}</div>
                <div className="caption">{industry.outcome.label}</div>
              </div>
            )}
          </div>
          <div className="md:col-span-5">
            <div className="relative w-full aspect-[4/5] overflow-hidden rounded-2xl bg-[#F5F4F0]">
              <img src={industry.image} alt={industry.name} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* What's in this suite */}
      <section className="py-16 md:py-24 bg-[#F5F4F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="caption mb-4">/ What's in this suite</div>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight max-w-3xl mb-12">
            Products across <span className="italic">{industry.pillars.length} pillars,</span> bundled into one workspace.
          </h2>
          <div className="space-y-4">
            {industry.pillars.map((bundle, i) => {
              const pillar = pillarBySlug[bundle.slug];
              if (!pillar) return null;
              return (
                <motion.div
                  key={bundle.slug}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 p-5 md:p-6 rounded-2xl bg-[#FDFCFB] border border-[#E8E6E1]"
                >
                  <div className="md:col-span-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] text-[#FDFCFB] flex items-center justify-center shrink-0">
                      <PillarIcon name={pillar.icon} className="w-4 h-4" />
                    </div>
                    <div>
                      <Link
                        to={`/platform/${pillar.slug}`}
                        className="font-serif-display text-xl tracking-tight text-[#1A1A1A] hover:underline underline-offset-4"
                      >
                        {pillar.name}
                      </Link>
                      <div className="caption !text-[10px] mt-1">{pillar.tagline}</div>
                    </div>
                  </div>
                  <div className="md:col-span-8 flex flex-wrap gap-2">
                    {bundle.items.map((it) => (
                      <span key={it} className="px-3 py-1.5 rounded-full bg-[#F5F4F0] text-[12px] text-[#1A1A1A]">
                        {it}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other industries */}
      <section className="py-16 md:py-20 bg-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="caption mb-4">/ Explore more industries</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {INDUSTRIES.filter((i) => i.slug !== industry.slug).slice(0, 8).map((ind) => (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}`}
                className="group p-4 rounded-xl border border-[#E8E6E1] hover:border-[#1A1A1A]/30 hover:bg-[#1A1A1A] hover:text-[#FDFCFB] transition-all"
              >
                <div className="font-serif-display text-base md:text-lg tracking-tight">{ind.name}</div>
                {ind.brand && (
                  <div className="font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#6B6A68] group-hover:text-[#FDFCFB]/60 mt-1">
                    {ind.brand}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <StickyCta />
    </main>
  );
}
